/**
 * Imperative head-metadata manager for the visitor runtime (B-6, per-page
 * SEO). Client-rendered meta: fine for Googlebot (executes JS), weaker for
 * plain-HTML scrapers — server-side injection is a documented follow-up.
 *
 * Precedence: page-level seoTitle/seoDescription/ogImageUrl override the
 * site-wide openGraph* config; page name is the final title fallback.
 */

type LangTextLike = string | Record<string, string> | null | undefined;

type PageMeta = {
  pageName?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  ogImageUrl?: string | null;
};

type ConfigMeta = {
  primaryLanguage?: string;
  openGraphTitle?: LangTextLike;
  openGraphDescription?: LangTextLike;
  openGraphImageUrl?: string | null;
  favIcon?: string | null;
  touchIcon?: string | null;
  noindex?: boolean;
};

const resolveLang = (value: LangTextLike, lang?: string): string | undefined => {
  if (!value) return undefined;
  if (typeof value === "string") return value.trim() || undefined;
  const direct = lang ? value[lang] : undefined;
  const first = direct ?? Object.values(value).find((v) => v && v.trim());
  return first?.trim() || undefined;
};

function upsertMeta(selector: string, create: () => HTMLMetaElement, content: string | undefined) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (content === undefined) {
    el?.remove();
    return;
  }
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * FIXED BD-6 (H26): swap the `<link rel="icon">` / apple-touch-icon to the
 * cockpit-configured favIcon/touchIcon. Previously the config fields were
 * shipped in bootstrap but had ZERO runtime consumers, so every published site
 * showed the bundled Veltric favicon. Updates the existing link(s) in place
 * (index.html ships defaults) or creates one when absent.
 */
function upsertIconLink(rel: "icon" | "apple-touch-icon", href: string | undefined): void {
  const selector =
    rel === "icon" ? 'link[rel~="icon"]:not([rel~="apple-touch-icon"])' : 'link[rel="apple-touch-icon"]';
  const existing = document.head.querySelectorAll<HTMLLinkElement>(selector);
  if (href === undefined) return;
  if (existing.length === 0) {
    const el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("href", href);
    el.setAttribute("data-wb-icon", rel);
    document.head.appendChild(el);
    return;
  }
  existing.forEach((el) => {
    el.setAttribute("href", href);
    el.setAttribute("data-wb-icon", rel);
  });
}

const namedMeta = (name: string) => () => {
  const el = document.createElement("meta");
  el.setAttribute("name", name);
  return el;
};
const propertyMeta = (property: string) => () => {
  const el = document.createElement("meta");
  el.setAttribute("property", property);
  return el;
};

/** Applies title/description/OG/robots for the current page. Idempotent. */
export function applyHeadMeta(page: PageMeta | null, config: ConfigMeta | null): void {
  const lang = config?.primaryLanguage;
  const siteTitle = resolveLang(config?.openGraphTitle, lang);
  const siteDescription = resolveLang(config?.openGraphDescription, lang);

  const title =
    page?.seoTitle?.trim() || page?.pageName?.trim() || siteTitle || "Website Runtime";
  const description = page?.seoDescription?.trim() || siteDescription;
  const ogImage = page?.ogImageUrl?.trim() || config?.openGraphImageUrl?.trim() || undefined;

  document.title = title;
  upsertMeta('meta[name="description"]', namedMeta("description"), description);
  upsertMeta('meta[property="og:title"]', propertyMeta("og:title"), title);
  upsertMeta('meta[property="og:description"]', propertyMeta("og:description"), description);
  upsertMeta('meta[property="og:image"]', propertyMeta("og:image"), ogImage);
  upsertMeta(
    'meta[name="robots"]',
    namedMeta("robots"),
    config?.noindex ? "noindex, nofollow" : undefined,
  );

  // Custom favicon / touch icon (H26).
  upsertIconLink("icon", config?.favIcon?.trim() || undefined);
  upsertIconLink("apple-touch-icon", config?.touchIcon?.trim() || undefined);
}

/** Clears everything applyHeadMeta manages (unmount path). */
export function resetHeadMeta(): void {
  document.title = "Website Runtime";
  for (const sel of [
    'meta[name="description"]',
    'meta[property="og:title"]',
    'meta[property="og:description"]',
    'meta[property="og:image"]',
    'meta[name="robots"]',
  ]) {
    document.head.querySelector(sel)?.remove();
  }
}
